import { Injectable } from '@angular/core';
import { CapacitorService, EnvironmentService, MsalConfigModel } from '@libs/shared/common';
import { AzureOAuthOptions } from '@libs/shared/models';

@Injectable({
  providedIn: 'root',
})
export class OAuthConfigService {
  private readonly msalUserConfig: MsalConfigModel;
  private readonly msalLlDirectoryUserConfig!: MsalConfigModel;
  private readonly redirectUriIos: string;
  private readonly redirectUriAndroid: string;

  public constructor(
    private readonly envService: EnvironmentService,
    private readonly capacitorService: CapacitorService) {
    this.msalUserConfig = this.envService.msalConfig();
    this.redirectUriIos = `msauth.${this.msalUserConfig.appId}://auth`;
    this.redirectUriAndroid = `msauth://${this.msalUserConfig.appId}/${this.msalUserConfig.androidHash}`;
    this.msalLlDirectoryUserConfig = this.envService.msalLekkerlandDirectoryUserConfig();
  }

  public getMsalConfig(): MsalConfigModel {
    return this.msalUserConfig;
  }

  public getUserAzureOAuthOptions(): AzureOAuthOptions {
    return {
      signInPolicy: this.msalUserConfig.signInPolicy,
      signUpPolicy: this.msalUserConfig.signUpPolicy,
      authorityHostname: this.msalUserConfig.authorityHostname,
      clientId: this.msalUserConfig.clientId,
      tenantName: this.msalUserConfig.tenantId,
      scope: [this.msalUserConfig.clientId],
      resetPasswordPolicy: this.msalUserConfig.resetPasswordPolicy,
      appId: this.msalUserConfig.appId,
      redirectUri: this.getRedirectUriForPlatform(),
    };
  }

  public getLlDirectoryUserAzureOAuthOptions(): AzureOAuthOptions {
    return {
      signInPolicy: this.msalLlDirectoryUserConfig.signInPolicy,
      signUpPolicy: this.msalLlDirectoryUserConfig.signUpPolicy,
      authorityHostname: this.msalLlDirectoryUserConfig.authorityHostname,
      clientId: this.msalLlDirectoryUserConfig.clientId,
      tenantName: this.msalLlDirectoryUserConfig.tenantId,
      scope: ['openid', 'profile', 'offline_access', 'api://f88c0bd8-adce-4509-abb0-deb8ce54c493/Agent'],
      resetPasswordPolicy: this.msalLlDirectoryUserConfig.resetPasswordPolicy,
      appId: this.msalLlDirectoryUserConfig.appId,
      redirectUri: this.msalLlDirectoryUserConfig.redirectUri,
    };
  }

  public getRedirectUriForPlatform(): string {
    if (!this.capacitorService.isCapacitorApp()) {
      return this.msalUserConfig.redirectUri;
    } else if (this.capacitorService.isRunningOnIOS()) {
      return this.redirectUriIos;
    } else {
      return this.redirectUriAndroid;
    }
  }

  public buildResetPasswordUrl(): string {
    const msalConfig: MsalConfigModel = this.getMsalConfig();
    const redirectUri: string = encodeURIComponent(this.getRedirectUriForPlatform());

    // eslint-disable-next-line max-len
    return `https://${msalConfig.authorityHostname}/${msalConfig.tenantId}/oauth2/v2.0/authorize?p=${msalConfig.resetPasswordPolicy}&client_id=${msalConfig.clientId}&redirect_uri=${redirectUri}&scope=${msalConfig.clientId}&response_type=code`;
  }

  public buildLogoutUrl(): string {
    const redirectUri: string = this.getRedirectUriForPlatform();

    // eslint-disable-next-line max-len
    return `https://${this.msalUserConfig.authorityHostname}/${this.msalUserConfig.tenantId}/oauth2/v2.0/logout?p=${this.msalUserConfig.signInPolicy}&client_id=${this.msalUserConfig.clientId}&nonce=defaultNonce&redirect_uri=${redirectUri}`;
  }
}
